'use client';

import React, { useState } from 'react';
import { ArrowDownTrayIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

interface LogoVariant {
  name: string;
  description: string;
  path: string;
  fileName: string;
}

export default function LogoDownload() {
  const [downloadedLogos, setDownloadedLogos] = useState<Set<string>>(new Set());

  const logoVariants: LogoVariant[] = [
    {
      name: 'Standard Logo',
      description: 'Primary hexagon logo for general use',
      path: '/assets/images/signaly-logo.svg',
      fileName: 'signaly-logo.svg'
    },
    {
      name: 'Logo with White Text',
      description: 'Logo with white text as used in the homepage header',
      path: '/assets/images/signaly-logo-with-text.svg',
      fileName: 'signaly-logo-with-text.svg'
    },
    {
      name: 'Logo with Black Text',
      description: 'Logo with black text for light backgrounds',
      path: '/assets/images/signaly_logo_black_text.png',
      fileName: 'signaly-logo-black-text.png'
    },
    {
      name: 'Square Logo',
      description: 'Square format with background for social media',
      path: '/assets/images/signaly-logo-square.svg',
      fileName: 'signaly-logo-square.svg'
    }
  ];

  const handleDownload = async (logo: LogoVariant) => {
    try {
      const response = await fetch(logo.path);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = logo.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Mark as downloaded
      setDownloadedLogos(prev => new Set(prev).add(logo.fileName));

      // Reset downloaded state after 3 seconds
      setTimeout(() => {
        setDownloadedLogos(prev => {
          const newSet = new Set(prev);
          newSet.delete(logo.fileName);
          return newSet;
        });
      }, 3000);
    } catch (error) {
      console.error('Error downloading logo:', error);
    }
  };

  return (
    <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-background to-gray-900">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            <span className="bg-gradient-to-r from-blue-400 to-violet-500 text-transparent bg-clip-text">
              Download Signaly Logo
            </span>
          </h2>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">
            Get official Signaly logo files for your presentations, articles, or marketing materials
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {logoVariants.map((logo, index) => {
            const isDownloaded = downloadedLogos.has(logo.fileName);

            return (
              <div
                key={index}
                className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-700 hover:border-blue-500 transition-all duration-300 hover:shadow-xl hover:shadow-blue-500/20"
              >
                {/* Logo Preview */}
                <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-8 mb-6 flex items-center justify-center min-h-[200px]">
                  <img
                    src={logo.path}
                    alt={`${logo.name} preview`}
                    className="max-w-full max-h-[150px] object-contain"
                  />
                </div>

                {/* Logo Details */}
                <div className="mb-4">
                  <h3 className="text-xl font-semibold text-white mb-2">
                    {logo.name}
                  </h3>
                  <p className="text-gray-400 text-sm">
                    {logo.description}
                  </p>
                </div>

                {/* Download Button */}
                <button
                  onClick={() => handleDownload(logo)}
                  disabled={isDownloaded}
                  className={`w-full py-3 px-4 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center gap-2 ${
                    isDownloaded
                      ? 'bg-green-600 text-white cursor-not-allowed' :'bg-gradient-to-r from-blue-600 to-violet-600 text-white hover:from-blue-700 hover:to-violet-700 hover:shadow-lg hover:shadow-blue-500/50'
                  }`}
                >
                  {isDownloaded ? (
                    <>
                      <CheckCircleIcon className="w-5 h-5" />
                      <span>Downloaded</span>
                    </>
                  ) : (
                    <>
                      <ArrowDownTrayIcon className="w-5 h-5" />
                      <span>Download SVG</span>
                    </>
                  )}
                </button>
              </div>
            );
          })}
        </div>

        {/* Usage Guidelines */}
        <div className="mt-12 bg-gray-800/30 backdrop-blur-sm rounded-2xl p-8 border border-gray-700">
          <h3 className="text-xl font-semibold text-white mb-4">
            Logo Usage Guidelines
          </h3>
          <ul className="space-y-2 text-gray-300">
            <li className="flex items-start gap-2">
              <span className="text-blue-400 mt-1">•</span>
              <span>Use these logos to represent Signaly in presentations, articles, or promotional materials</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-400 mt-1">•</span>
              <span>Maintain the aspect ratio and do not distort or modify the logo design</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-400 mt-1">•</span>
              <span>Ensure adequate spacing around the logo for optimal visibility</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-400 mt-1">•</span>
              <span>For questions about logo usage, contact us at signalyapp@proton.me</span>
            </li>
          </ul>
        </div>
      </div>
    </section>
  );
}